import { useGetAdmins } from "@entities/admin";
import { ToastService } from "@shared/lib/services";
import { IGetOrganisationPermissions } from "@shared/lib/types";
import { Button } from "@shared/ui/Button";
import { Avatar, Flex, Form, Select } from "antd";
import Meta from "antd/es/card/Meta";
import { useEffect, useState } from "react";
import useAddUsersToOrganisation from "../lib/hooks/useAddUsersToOrganisation";
import useGetOrganisationById from "../lib/hooks/useGetOrganisationById";

export default function OrganisationAccountManagerDetails({
  organisationId,
  userPermissions,
  isEdit,
  onCancel,
}: {
  organisationId: string;
  userPermissions: IGetOrganisationPermissions | null;
  isEdit?: boolean;
  onCancel: () => void;
}) {
  const [form] = Form.useForm();
  const { addUsersToOrganisation, isLoading, isSuccess, error } =
    useAddUsersToOrganisation();

  const [searchQuery, setSearchQuery] = useState<string>(
    isEdit
      ? userPermissions?.user?.firstName +
          " " +
          userPermissions?.user?.lastName || ""
      : ""
  );

  const { organisation } = useGetOrganisationById(organisationId);

  const { admins: adminsData, isFetching } = useGetAdmins(
    10,
    1,
    [],
    searchQuery
  );
  const admins = adminsData?.data || [];

  useEffect(() => {
    if (isSuccess) {
      onCancel();
      ToastService.success(
        "Member " + (isEdit ? "updated" : "created") + " successfully"
      );
    }
  }, [isSuccess]);
  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };
  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark="optional"
      initialValues={
        isEdit
          ? {
              userId: userPermissions?.userId,
              brandIds: userPermissions?.brandIds,
            }
          : {}
      }
      onFinish={(values) => {
        addUsersToOrganisation({
          userId: values.userId,
          brandOrganisationId: organisationId || "",
          brandIds: values.brandIds,
        });
      }}
    >
      <Form.Item
        name="userId"
        label="User"
        rules={[
          {
            required: true,
            message: "Please select a user.",
          },
        ]}
      >
        <Select
          showSearch
          onSearch={handleSearch}
          disabled={isEdit}
          style={{ height: "fit-content" }}
          placeholder="Select User"
          filterOption={false}
          loading={isFetching}
          options={admins?.map((admin) => ({
            label: (
              <Meta
                style={{ display: "flex", gap: 10, alignItems: "center" }}
                title={admin.firstName + " " + admin.lastName}
                avatar={<Avatar src={admin.profilePicUrl} />}
                description={admin.role}
              />
            ),
            value: admin._id,
          }))}
        />
      </Form.Item>
      <Form.Item
        name="brandIds"
        rules={[
          {
            required: true,
            message: "Please select atleast one brand.",
          },
        ]}
        label={
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            Brand
            <Button
              type="link"
              onClick={() => {
                form.setFieldValue(
                  "brandIds",
                  organisation?.brands?.map((brand) => brand._id)
                );
              }}
            >
              Select All
            </Button>
          </Flex>
        }
      >
        <Select
          mode="multiple"
          placeholder="Select Brand"
          options={organisation?.brands?.map((brand) => ({
            label: brand.name,
            value: brand._id,
          }))}
          optionFilterProp="label"
          maxTagCount={3}
        />
      </Form.Item>

      <Form.Item>
        <Flex justify="flex-end" gap={10}>
          <Button onClick={() => onCancel()}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? "Update Member" : "Add Member"}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );
}
